import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api, { formatDate } from '../lib/api';
import { Button } from './ui/button';
import { Upload, FileText, X, Download, Paperclip } from 'lucide-react';
import { toast } from 'sonner';

export default function FileUpload({ entityType, entityId, canUpload = true }) {
  const { t } = useTranslation();
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (entityId) loadAttachments();
  }, [entityId]);

  const loadAttachments = async () => {
    try {
      const { data } = await api.get('/attachments', {
        params: { entity_type: entityType, entity_id: entityId },
      });
      setAttachments(data);
    } catch (err) {
      /* silent - attachments are supplementary */
    }
  };

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;

    setUploading(true);
    for (const file of files) {
      if (file.size > (file.type.startsWith('video/') ? 50 : 15) * 1024 * 1024) {
        toast.error(`${file.name} - ${t('attachments.maxSize')}`);
        continue;
      }
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('entity_type', entityType);
        formData.append('entity_id', entityId);
        await api.post('/attachments/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success(t('toast.saved'));
      } catch (err) {
        toast.error(err.response?.data?.detail || err.response?.data?.message || t('attachments.uploadFailed'));
      }
    }
    setUploading(false);
    loadAttachments();
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/attachments/${id}`);
      toast.success(t('toast.deleted'));
      loadAttachments();
    } catch (err) {
      toast.error(t('toast.error'));
    }
  };

  const getFileUrl = (att) => att.download_url || `/api/uploads/${att.filename}`;
  const isImage = (mime) => mime?.startsWith('image/');

  return (
    <div className="space-y-3" data-testid="file-upload-section">
      {canUpload && (
        <div className="flex items-center gap-3">
          <Button
            size="sm"
            variant="outline"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="upload-file-btn"
          >
            <Upload className="w-3.5 h-3.5 me-1.5" />
            {uploading ? t('attachments.uploading') : t('attachments.upload')}
          </Button>
          <input
            ref={fileRef}
            type="file"
            multiple
            accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,video/mp4,video/quicktime,video/x-msvideo,video/webm"
            className="hidden"
            onChange={handleUpload}
            data-testid="file-input"
          />
          <span className="text-xs text-muted-foreground">{t('attachments.allowedTypes')}</span>
        </div>
      )}

      {attachments.length > 0 && (
        <div className="space-y-2" data-testid="attachments-list">
          {attachments.map((a) => (
            <div
              key={a.id}
              className="flex items-center gap-3 p-2.5 rounded-lg border border-border bg-muted/50 hover:border-border transition-colors"
              data-testid={`attachment-${a.id}`}
            >
              {isImage(a.mime_type) ? (
                <a href={getFileUrl(a)} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                  <img src={getFileUrl(a)} alt={a.original_name} className="w-12 h-12 object-cover rounded border border-border" />
                </a>
              ) : (
                <a href={getFileUrl(a)} target="_blank" rel="noopener noreferrer" className="w-12 h-12 flex items-center justify-center rounded bg-zinc-200 flex-shrink-0">
                  <FileText className="w-5 h-5 text-muted-foreground" />
                </a>
              )}

              <div className="flex-1 min-w-0">
                <p className="text-sm text-muted-foreground font-medium truncate">{a.original_name}</p>
                <p className="text-xs text-muted-foreground">
                  {(a.size / 1024).toFixed(1)} KB &middot; {a.uploaded_by_name} &middot; {formatDate(a.created_at)}
                </p>
              </div>

              <a href={getFileUrl(a)} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-muted-foreground p-1" data-testid={`download-att-${a.id}`}>
                <Download className="w-4 h-4" />
              </a>
              {canUpload && (
                <button onClick={() => handleDelete(a.id)} className="text-muted-foreground hover:text-rose-500 p-1" data-testid={`delete-att-${a.id}`}>
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {attachments.length === 0 && (
        <p className="text-xs text-muted-foreground flex items-center gap-1.5">
          <Paperclip className="w-3 h-3" /> {t('attachments.noAttachments')}
        </p>
      )}
    </div>
  );
}
